import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { themeAtom } from "../../shared/atoms";
import { DriverInputRowProps, RowRef } from "../models/DriverInputRow.model";
import { KeyDriver } from "../models/UtilFunctions.model";
import { RouteParams } from "../models/VoyagePnl.model";
import {
  parseNumber,
  formatCurrency,
  calculateImpactRaw,
  PnlContext,
  extractRowSubNames,
  getPnlRowsStyling,
  getDriverLine,
  hasVoyageDriversStoreSnapshot,
  continueClickedAtomFamily,
  setDriverLine,
  canonicalize,
} from "../utils/UtilFunctions";
import BlurredAlert from "./WarningMessage";
import { useAtomValue } from "jotai";
import { useImperativeHandle, forwardRef, useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const DriverInputRow = forwardRef<RowRef, DriverInputRowProps>(
  ({ quantityName, optionName, rateName, impactName, categoriesAndOptions, onRowChanged }, ref) => {
    const theme = useAtomValue(themeAtom);

    const [rate, setRate] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("");

    const [unit, setUnit] = useState<string>("USD");
    const [showAlert, setShowAlert] = useState(false);
    const [isRateFocused, setIsRateFocused] = useState<boolean>(false);

    const voyagePnlData = useAtomValue(PnlContext);
    const { tcCode, voyageNo, isApprover } = useParams<RouteParams>();
    const { hasFeature } = useFeatureAccess();
    const voyageScope = useMemo(() => ({ tcCode, voyageNo }), [tcCode, voyageNo]);
    const continueClicked = useAtomValue(continueClickedAtomFamily(voyageScope));
    const isApprovalMode = isApprover === "approval";
    const hasStoreSnapshotOnLoad = useMemo(
      () => !isApprovalMode && hasVoyageDriversStoreSnapshot(tcCode, voyageNo),
      [isApprovalMode, tcCode, voyageNo],
    );

    const userCanReviewPnl = isApprover === "approval" && hasFeature(FEATURE_KEYS.REVIEW_PNL);

    const { categoryName, driverName } = extractRowSubNames(quantityName);

    const extractedDriver = useMemo(
      () => voyagePnlData?.addDrivers?.find((d) => canonicalize(d.driver) === canonicalize(driverName)),
      [voyagePnlData, driverName],
    );
    const extractedRow = useMemo(
      () =>
        extractedDriver?.driverCategoryImpact.find(
          (item) => canonicalize(item.category) === canonicalize(categoryName),
        ),
      [extractedDriver, categoryName],
    );

    const optionsCategory = useMemo(() => optionName.split("-")[0].replace(/_/g, " "), [optionName]);

    const options = useMemo(
      () => categoriesAndOptions.find((item) => item.category === optionsCategory)?.options ?? ["USD", "days", "mt"],
      [categoriesAndOptions, optionsCategory],
    );

    const getDefaultUnit = useCallback((): string => options[0] ?? "USD", [options]);

    // Normalize categories so "Freight rate" => "freight"
    const normalizeCategory = (s?: string) =>
      (s ?? "")
        .toLowerCase()
        .replace(/\s*rate\s*$/i, "")
        .trim();

    const defaultRateForCategory = useMemo((): string => {
      const normCat = normalizeCategory(categoryName);
      const kd = (voyagePnlData?.keyDrivers as KeyDriver[] | undefined)?.find(
        (k) => normalizeCategory(k?.category) === normCat,
      );
      return kd?.rate != null ? String(kd.rate) : "";
    }, [voyagePnlData?.keyDrivers, categoryName]);

    const getDefaultRateForCategory = useCallback((): string => defaultRateForCategory, [defaultRateForCategory]);

    const pnLRowClass = () => {
      const background = getPnlRowsStyling(categoryName);
      if (background === "Green") return theme.voyPnl.rowGreenBg;
      if (background === "Red") return theme.voyPnl.rowRedBg;
      if (background === "Default") return theme.home.tailwind.backgound;
      return "";
    };

    // ---- Initialization: load from voyage draft store, backend data, or defaults ----
    useEffect(() => {
      const saved = isApprovalMode ? undefined : getDriverLine(tcCode, voyageNo, driverName, categoryName);

      const effectiveUnit = hasStoreSnapshotOnLoad
        ? saved?.unit ?? getDefaultUnit()
        : saved?.unit ?? extractedRow?.unit ?? getDefaultUnit();
      setUnit(effectiveUnit);

      const hasSavedQty = !!saved && Object.prototype.hasOwnProperty.call(saved, "qty");
      const hasSavedRate = !!saved && Object.prototype.hasOwnProperty.call(saved, "rate");

      const savedQty = typeof saved?.qty === "number" && !isNaN(saved.qty) ? saved.qty : undefined;
      const extractedQty =
        typeof extractedRow?.qty === "number" && !isNaN(extractedRow.qty) ? extractedRow.qty : undefined;

      setQuantity(
        hasSavedQty
          ? savedQty !== undefined
            ? String(savedQty)
            : ""
          : hasStoreSnapshotOnLoad
            ? ""
            : extractedQty !== undefined
              ? String(extractedQty)
              : "",
      );

      if (hasSavedRate) {
        if (typeof saved?.rate === "number" && !isNaN(saved.rate)) {
          setRate(String(saved.rate));
        } else {
          setRate(getDefaultRateForCategory() || "");
        }
      } else if (hasStoreSnapshotOnLoad) {
        setRate(getDefaultRateForCategory() || "");
      } else if (typeof extractedRow?.rate === "number" && !isNaN(extractedRow.rate)) {
        setRate(String(extractedRow.rate));
      } else {
        setRate(getDefaultRateForCategory() || "");
      }
    }, [
      tcCode,
      voyageNo,
      driverName,
      categoryName,
      extractedRow?.unit,
      extractedRow?.qty,
      extractedRow?.rate,
      getDefaultUnit,
      getDefaultRateForCategory,
      hasStoreSnapshotOnLoad,
      isApprovalMode,
    ]);

    // Derived formatted impact (no impact state)
    const formattedImpact = useMemo(() => {
      const q = parseNumber(quantity);
      if (!quantity.trim() || isNaN(q)) return "-";

      if (unit === "USD") {
        return formatCurrency(q);
      }

      const r = parseNumber(rate);
      if (!rate.trim() || isNaN(r)) return "-";

      return formatCurrency(r * q);
    }, [unit, rate, quantity]);

    // Persist (and notify parent) whenever inputs change
    useEffect(() => {
      const rawImpact = calculateImpactRaw(unit, rate, quantity);
      const parsedRate = parseNumber(rate);
      const parsedQty = parseNumber(quantity);
      const impactNum = rawImpact ? parseNumber(rawImpact) : NaN;

      if (!isApprovalMode) {
        setDriverLine(tcCode, voyageNo, driverName, categoryName, {
          unit,
          rate: rate.trim() && !isNaN(parsedRate) ? parsedRate : null,
          qty: quantity.trim() && !isNaN(parsedQty) ? parsedQty : undefined,
          impact: !isNaN(impactNum) ? impactNum : undefined,
        });
      }

      onRowChanged?.();
    }, [unit, rate, quantity, tcCode, voyageNo, driverName, categoryName, onRowChanged, isApprovalMode]);

    // Imperative reset used by KeyDriver's driver-scoped reset
    useImperativeHandle(ref, () => ({
      resetRowValues,
    }));

    const resetRowValues = () => {
      setQuantity("");
      setUnit(getDefaultUnit());
      setRate(getDefaultRateForCategory());

      // Clear from Drivers store for this category
      if (!isApprovalMode) {
        setDriverLine(tcCode, voyageNo, driverName, categoryName, {
          qty: undefined,
          unit: undefined,
          rate: undefined,
          impact: undefined,
        });
      }

      onRowChanged?.();
    };

    // Numeric validation
    const isValidNumberInput = (value: string) => /^-?\d*\.?\d*$/.test(value);

    const handleValidatedInputChange = (
      value: string,
      setter: React.Dispatch<React.SetStateAction<string>>,
      fieldName?: string,
    ) => {
      if (isValidNumberInput(value)) {
        setter(value);
      } else {
        console.warn(`Invalid input in ${fieldName ?? "unknown field"}`);
        setShowAlert(true);
      }
    };

    // Format rate for display when not focused
    const formatRateDisplay = (): string => {
      if (unit === "USD" || !rate.trim()) return "-";
      const suffixMap: Record<string, string> = {
        days: "$/d",
        mt: "$/mt",
      };
      const suffix = suffixMap[unit] || "";
      return suffix ? `${rate} ${suffix}` : rate;
    };

    // Close alert if clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const alertBox = document.getElementById("alert-box");
        if (alertBox && !alertBox.contains(event.target as Node)) {
          setShowAlert(false);
        }
      };
      if (showAlert) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showAlert]);

    const isReadOnly = isApprovalMode || continueClicked || userCanReviewPnl;
    const isRateDisabled = unit === "USD";

    return (
      <>
        <tr
          className={twMerge(
            "text:[12px] relative h-[44px] border-t",
            pnLRowClass(),
            theme.uwhcAssistant.text,
            theme.uwhcAssistant.border,
          )}
        >
          {/* Quantity + Unit */}
          <td className="flex h-[43px] items-center p-0">
            <input
              name={quantityName}
              value={quantity}
              readOnly={isReadOnly}
              onChange={(e) => handleValidatedInputChange(e.target.value, setQuantity, "quantity")}
              type="text"
              className={twMerge("w-[80px] px-2 text-right outline-none", pnLRowClass(), theme.uwhcAssistant.border)}
            />

            {continueClicked ? (
              quantity ? (
                <>
                  <input name={optionName} value={unit} type="hidden" />
                  <span className="flex h-[43px] w-[60px] items-center">{unit}</span>
                </>
              ) : (
                <span className="flex h-[43px] w-[60px] items-center justify-center">-</span>
              )
            ) : (
              <select
                name={optionName}
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                disabled={isReadOnly}
                className={twMerge(
                  "h-[43px] w-[60px] outline-none",
                  continueClicked
                    ? twMerge(
                        "cursor-default appearance-none text-left",
                        theme.home.tailwind.panelBackground,
                        theme.uwhcAssistant.text,
                      )
                    : twMerge("border-x text-center", theme.uwhcAssistant.border, theme.home.tailwind.backgound),
                  userCanReviewPnl && theme.uwhcAssistant.text,
                )}
              >
                {options.map((opt) => (
                  <option key={opt} value={opt} className={theme.home.tailwind.backgound}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
          </td>

          {/* Rate */}
          {continueClicked ? (
            <input type="hidden" name={rateName} value={rate} />
          ) : (
            <td className="p-0">
              <input
                name={rateName}
                value={isRateFocused ? rate : formatRateDisplay()}
                onChange={(e) => handleValidatedInputChange(e.target.value, setRate, "rate")}
                onFocus={() => !userCanReviewPnl && setIsRateFocused(true)}
                onBlur={() => setIsRateFocused(false)}
                type="text"
                disabled={isRateDisabled}
                readOnly={userCanReviewPnl}
                className={twMerge(
                  "w-[120px] px-4 text-right outline-none",
                  pnLRowClass(),
                  isRateDisabled ? twMerge(theme.vesselDetails.info, "opacity-50") : "",
                )}
              />
            </td>
          )}

          {/* Impact */}
          <td className="p-0">
            <input
              name={impactName}
              value={formattedImpact}
              readOnly
              type="text"
              className={twMerge("w-[120px] px-4 text-right outline-none", pnLRowClass())}
            />
          </td>
        </tr>

        {/* Warning Message in case non numeric values are entered */}
        {showAlert && <BlurredAlert message="Please use only numbers!" onClose={() => setShowAlert(false)} />}
      </>
    );
  },
);

export default DriverInputRow;
